import bridge from '@vkontakte/vk-bridge';
import type { WebApp } from '@twa-dev/types';

export interface VirtualButtonState {
  text: string;
  color: string;
  textColor: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  position?: 'left' | 'right' | 'top' | 'bottom';
  onClickCallbacks: (() => void)[];
}

export const mainButtonState: VirtualButtonState = {
  text: '',
  color: '#2481cc',
  textColor: '#ffffff',
  isVisible: false,
  isActive: true,
  isProgressVisible: false,
  onClickCallbacks: [],
};

export const secondaryButtonState: VirtualButtonState = {
  text: '',
  color: '#f1f1f1',
  textColor: '#000000',
  isVisible: false,
  isActive: true,
  isProgressVisible: false,
  position: 'left',
  onClickCallbacks: [],
};

const buttonListeners = new Set<() => void>();
const themeListeners = new Set<(theme: 'light' | 'dark') => void>();

export const subscribeToButtons = (listener: () => void) => {
  buttonListeners.add(listener);
  return () => { buttonListeners.delete(listener); };
};

export const notifyButtons = () => {
  buttonListeners.forEach(l => l());
};

export const subscribeToVkTheme = (listener: (theme: 'light' | 'dark') => void) => {
  themeListeners.add(listener);
  return () => { themeListeners.delete(listener); };
};

const notifyTheme = (theme: 'light' | 'dark') => {
  themeListeners.forEach(l => l(theme));
};

export async function getVkLocationCoords(): Promise<[number, number] | null> {
  try {
    const result = await (bridge as any).send('VKWebAppGetGeodata');
    const lat = Number(result?.lat);
    const lon = Number(result?.long);
    return Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null;
  } catch {
    return null;
  }
}

export async function createVkWebAppAdapter(
  vkInitData: string,
  vkUserInfo: any
): Promise<Partial<WebApp> & Record<string, any>> {
  const params = new URLSearchParams(vkInitData);

  // vk_theme from URL is unreliable — use VKWebAppUpdateConfig event instead.
  // Start with URL param as initial fallback until the event fires.
  const vkThemeParam = params.get('vk_theme') || 'light';
  let colorScheme: 'light' | 'dark' = vkThemeParam.includes('dark') ? 'dark' : 'light';

  const mockUser = {
    id: 2000000000000 + parseInt(params.get('vk_user_id') || '0', 10),
    first_name: vkUserInfo?.first_name || 'VK',
    last_name: vkUserInfo?.last_name || 'User',
    username: vkUserInfo?.screen_name || '',
    language_code: params.get('vk_language') || 'ru',
    is_premium: false,
    photo_url: vkUserInfo?.photo_200 || vkUserInfo?.photo_max || undefined,
  };

  const mockInitDataUnsafe = {
    user: mockUser,
    auth_date: Math.floor(Date.now() / 1000),
    hash: params.get('sign') || '',
    query_id: 'vk_query_' + params.get('vk_user_id'),
  };

  const backButtonCallbacks: (() => void)[] = [];
  bridge.subscribe((event: any) => {
    if (!event.detail) return;
    const { type, data } = event.detail;

    if (type === 'VKWebAppBackButtonPressed') {
      backButtonCallbacks.forEach(cb => cb());
    }

    // VK sends VKWebAppUpdateConfig on load and when user switches theme.
    // data.appearance: 'light' | 'dark'; data.insets: { top, bottom, left, right }
    if (type === 'VKWebAppUpdateConfig') {
      const appearance: 'light' | 'dark' =
        data?.appearance === 'dark' ||
        (typeof data?.scheme === 'string' && data.scheme.includes('dark'))
          ? 'dark'
          : 'light';
      if (appearance !== colorScheme) {
        colorScheme = appearance;
        adapter.colorScheme = appearance;
        notifyTheme(appearance);
      }

      // VK passes safe area insets so fixed headers don't overlap VK's own UI
      if (data?.insets) {
        const el = document.documentElement;
        el.style.setProperty('--tg-safe-area-inset-top', `${data.insets.top ?? 0}px`);
        el.style.setProperty('--tg-safe-area-inset-bottom', `${data.insets.bottom ?? 0}px`);
        el.style.setProperty('--tg-safe-area-inset-left', `${data.insets.left ?? 0}px`);
        el.style.setProperty('--tg-safe-area-inset-right', `${data.insets.right ?? 0}px`);
        // VK's insets already include the VK header bar, so set content inset to 0
        el.style.setProperty('--tg-content-safe-area-inset-top', '0px');
      }
    }
  });

  const adapter: any = {
    initData: vkInitData,
    initDataUnsafe: mockInitDataUnsafe as any,
    version: '8.0',
    platform: 'vk',
    isVk: true,
    colorScheme,
    themeParams: {
      bg_color: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
      text_color: colorScheme === 'dark' ? '#ffffff' : '#000000',
      hint_color: '#999999',
      link_color: '#4986cc',
      button_color: '#4986cc',
      button_text_color: '#ffffff',
      secondary_bg_color: colorScheme === 'dark' ? '#15181b' : '#f1f1f1',
      header_bg_color: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
      accent_text_color: '#4986cc',
      section_bg_color: colorScheme === 'dark' ? '#15181b' : '#ffffff',
      section_header_text_color: '#999999',
      subtitle_text_color: '#999999',
      destructive_text_color: '#ff3b30',
      section_separator_color: '#e5e5e5',
      bottom_bar_bg_color: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
    },
    isExpanded: true,
    viewportHeight: window.innerHeight,
    viewportStableHeight: window.innerHeight,
    headerColor: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
    backgroundColor: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
    bottomBarColor: colorScheme === 'dark' ? '#0d0f11' : '#ffffff',
    isClosingConfirmationEnabled: false,
    isVerticalSwipesEnabled: true,
    isFullscreen: false,
    isActive: true,
    safeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 },
    contentSafeAreaInset: { top: 0, bottom: 0, left: 0, right: 0 },

    ready: () => {
      console.log('[VK Adapter] WebApp.ready()');
    },
    expand: () => {
      console.log('[VK Adapter] WebApp.expand()');
    },
    close: () => {
      console.log('[VK Adapter] WebApp.close()');
      (bridge as any).send('VKWebAppClose', { status: 'success' });
    },
    enableClosingConfirmation: () => {
      console.log('[VK Adapter] WebApp.enableClosingConfirmation()');
    },
    disableClosingConfirmation: () => {
      console.log('[VK Adapter] WebApp.disableClosingConfirmation()');
    },

    HapticFeedback: {
      impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') {
        let vkStyle: 'light' | 'medium' | 'heavy' = 'medium';
        if (style === 'light' || style === 'soft') vkStyle = 'light';
        if (style === 'heavy' || style === 'rigid') vkStyle = 'heavy';
        (bridge as any).send('VKWebAppTapticImpactOccurred', { style: vkStyle }).catch(() => {});
        return this;
      },
      notificationOccurred(type: 'error' | 'success' | 'warning' = 'success') {
        let vkType: 'success' | 'warning' | 'error' = 'success';
        if (type === 'error') vkType = 'error';
        if (type === 'warning') vkType = 'warning';
        (bridge as any).send('VKWebAppTapticNotificationOccurred', { type: vkType }).catch(() => {});
        return this;
      },
      selectionChanged() {
        (bridge as any).send('VKWebAppTapticSelectionChanged').catch(() => {});
        return this;
      },
    },

    openLink: (url: string, options?: { try_instant_view?: boolean }) => {
      console.log('[VK Adapter] WebApp.openLink', url, options);
      (bridge as any).send('VKWebAppOpenURL', { url }).catch(() => {
        window.open(url, '_blank');
      });
    },
    openTelegramLink: (url: string) => {
      console.log('[VK Adapter] WebApp.openTelegramLink', url);
      (bridge as any).send('VKWebAppOpenURL', { url }).catch(() => {
        window.open(url, '_blank');
      });
    },

    showAlert: (message: string, callback?: () => void) => {
      console.log('[VK Adapter] WebApp.showAlert', message);
      (bridge as any).send('VKWebAppShowAlert', { message })
        .then(() => callback?.())
        .catch(() => {
          alert(message);
          callback?.();
        });
    },

    showConfirm: (message: string, callback?: (confirmed: boolean) => void) => {
      console.log('[VK Adapter] WebApp.showConfirm', message);
      const confirmed = window.confirm(message);
      callback?.(confirmed);
    },

    showPopup: (params: any, callback?: (id?: string) => void) => {
      console.log('[VK Adapter] WebApp.showPopup', params);
      const confirmed = window.confirm(params.message);
      callback?.(confirmed ? 'ok' : 'cancel');
    },

    MainButton: {
      get text() { return mainButtonState.text; },
      get color() { return mainButtonState.color as `#${string}`; },
      get textColor() { return mainButtonState.textColor as `#${string}`; },
      get isVisible() { return mainButtonState.isVisible; },
      get isActive() { return mainButtonState.isActive; },
      get isProgressVisible() { return mainButtonState.isProgressVisible; },
      setText(text: string) {
        mainButtonState.text = text;
        notifyButtons();
        return this;
      },
      onClick(callback: () => void) {
        mainButtonState.onClickCallbacks.push(callback);
        return this;
      },
      offClick(callback: () => void) {
        mainButtonState.onClickCallbacks = mainButtonState.onClickCallbacks.filter(c => c !== callback);
        return this;
      },
      show() {
        mainButtonState.isVisible = true;
        notifyButtons();
        return this;
      },
      hide() {
        mainButtonState.isVisible = false;
        notifyButtons();
        return this;
      },
      enable() {
        mainButtonState.isActive = true;
        notifyButtons();
        return this;
      },
      disable() {
        mainButtonState.isActive = false;
        notifyButtons();
        return this;
      },
      showProgress(_leaveActive?: boolean) {
        mainButtonState.isProgressVisible = true;
        notifyButtons();
        return this;
      },
      hideProgress() {
        mainButtonState.isProgressVisible = false;
        notifyButtons();
        return this;
      },
      setParams(params: {
        text?: string;
        color?: string;
        text_color?: string;
        is_active?: boolean;
        is_visible?: boolean;
      }) {
        if (params.text !== undefined) mainButtonState.text = params.text;
        if (params.color !== undefined) mainButtonState.color = params.color;
        if (params.text_color !== undefined) mainButtonState.textColor = params.text_color;
        if (params.is_active !== undefined) mainButtonState.isActive = params.is_active;
        if (params.is_visible !== undefined) mainButtonState.isVisible = params.is_visible;
        notifyButtons();
        return this;
      },
    },

    SecondaryButton: {
      get text() { return secondaryButtonState.text; },
      get color() { return secondaryButtonState.color as `#${string}`; },
      get textColor() { return secondaryButtonState.textColor as `#${string}`; },
      get isVisible() { return secondaryButtonState.isVisible; },
      get isActive() { return secondaryButtonState.isActive; },
      get isProgressVisible() { return secondaryButtonState.isProgressVisible; },
      get position() { return secondaryButtonState.position || 'left'; },
      setText(text: string) {
        secondaryButtonState.text = text;
        notifyButtons();
        return this;
      },
      onClick(callback: () => void) {
        secondaryButtonState.onClickCallbacks.push(callback);
        return this;
      },
      offClick(callback: () => void) {
        secondaryButtonState.onClickCallbacks = secondaryButtonState.onClickCallbacks.filter(c => c !== callback);
        return this;
      },
      show() {
        secondaryButtonState.isVisible = true;
        notifyButtons();
        return this;
      },
      hide() {
        secondaryButtonState.isVisible = false;
        notifyButtons();
        return this;
      },
      enable() {
        secondaryButtonState.isActive = true;
        notifyButtons();
        return this;
      },
      disable() {
        secondaryButtonState.isActive = false;
        notifyButtons();
        return this;
      },
      showProgress(_leaveActive?: boolean) {
        secondaryButtonState.isProgressVisible = true;
        notifyButtons();
        return this;
      },
      hideProgress() {
        secondaryButtonState.isProgressVisible = false;
        notifyButtons();
        return this;
      },
      setParams(params: {
        text?: string;
        color?: string;
        text_color?: string;
        is_active?: boolean;
        is_visible?: boolean;
        position?: 'left' | 'right' | 'top' | 'bottom';
      }) {
        if (params.text !== undefined) secondaryButtonState.text = params.text;
        if (params.color !== undefined) secondaryButtonState.color = params.color;
        if (params.text_color !== undefined) secondaryButtonState.textColor = params.text_color;
        if (params.is_active !== undefined) secondaryButtonState.isActive = params.is_active;
        if (params.is_visible !== undefined) secondaryButtonState.isVisible = params.is_visible;
        if (params.position !== undefined) secondaryButtonState.position = params.position;
        notifyButtons();
        return this;
      },
    },

    BackButton: {
      isVisible: false,
      onClick(callback: () => void) {
        backButtonCallbacks.push(callback);
        return this;
      },
      offClick(callback: () => void) {
        const idx = backButtonCallbacks.indexOf(callback);
        if (idx !== -1) backButtonCallbacks.splice(idx, 1);
        return this;
      },
      show() {
        this.isVisible = true;
        (bridge as any).send('VKWebAppEnableSwipeBack').catch(() => {});
        return this;
      },
      hide() {
        this.isVisible = false;
        (bridge as any).send('VKWebAppDisableSwipeBack').catch(() => {});
        return this;
      },
    },

    SettingsButton: {
      isVisible: false,
      onClick(_callback: () => void) {
        console.log('[VK Adapter] SettingsButton.onClick registered');
        return this;
      },
      offClick(_callback: () => void) {
        console.log('[VK Adapter] SettingsButton.offClick registered');
        return this;
      },
      show() {
        console.log('[VK Adapter] SettingsButton.show');
        return this;
      },
      hide() {
        console.log('[VK Adapter] SettingsButton.hide');
        return this;
      },
    },

    CloudStorage: {
      setItem: (key: string, value: string): Promise<boolean> => {
        return (bridge as any).send('VKWebAppStorageSet', { key, value }).then((res: any) => res.result).catch(() => false);
      },
      getItem: (key: string): Promise<string | null> => {
        return (bridge as any).send('VKWebAppStorageGet', { keys: [key] }).then((res: any) => res.keys[0]?.value || null).catch(() => null);
      },
      getItems: (keys: string[]): Promise<Record<string, string>> => {
        return (bridge as any).send('VKWebAppStorageGet', { keys }).then((res: any) => {
          const result: Record<string, string> = {};
          res.keys.forEach((k: any) => {
            if (k.value) result[k.key] = k.value;
          });
          return result;
        }).catch(() => ({}));
      },
      removeItem: (key: string): Promise<boolean> => {
        return (bridge as any).send('VKWebAppStorageSet', { key, value: '' }).then((res: any) => res.result).catch(() => false);
      },
      removeItems: (keys: string[]): Promise<boolean> => {
        const promises = keys.map(k => (bridge as any).send('VKWebAppStorageSet', { key: k, value: '' }));
        return Promise.all(promises).then(() => true).catch(() => false);
      },
      getKeys: (): Promise<string[]> => {
        return (bridge as any).send('VKWebAppStorageGetKeys', { count: 100, offset: 0 }).then((res: any) => res.keys).catch(() => []);
      },
    },
    
    requestFullscreen: () => {
      console.log('[VK Adapter] requestFullscreen');
    },
    exitFullscreen: () => {
      console.log('[VK Adapter] exitFullscreen');
    },
  };

  return adapter as any;
}
